// src/components/onboarding/OnboardingFlow.tsx
import React, { useState, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUser } from '../../contexts/UserContext';
import userService from '../../services/userService'; // This service now includes the token in its requests

import WelcomeScreen from './WelcomeScreen';
import Terms from './Terms';
import BiometricVerification from './BiometricVerification';
import VoiceVerification from './VoiceVerification';
import AuthSuccess from './AuthSuccess';

const FlowContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  border: 2px dashed #ccc;
  background-color: #fff;
  min-height: 400px; // Add minimum height to prevent layout shifts
  position: relative;
`;

const ProgressIndicator = styled.p`
  text-align: center;
  font-size: 0.9rem;
  color: #555;
  margin-top: 20px;
  position: absolute;
  bottom: 20px;
  width: 100%;
  left: 0;
`;

// Memoize the step components to prevent unnecessary re-renders
const MemoizedWelcomeScreen = memo(WelcomeScreen);
const MemoizedTerms = memo(Terms);
const MemoizedBiometricVerification = memo(BiometricVerification);
const MemoizedVoiceVerification = memo(VoiceVerification);
const MemoizedAuthSuccess = memo(AuthSuccess);

const OnboardingFlow: React.FC = () => {
  // Step state: 1 = Welcome, 2 = Terms, 3 = Biometric, 4 = Voice, 5 = Auth Success.
  const [step, setStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { setProfile } = useUser();

  const nextStep = useCallback(() => {
    setStep(prev => Math.min(prev + 1, 5));
  }, []);

  const handleFinish = useCallback(async () => {
    if (isLoading) return; // Prevent multiple calls
    
    setIsLoading(true);
    try {
      const updatedUser = await userService.completeOnboarding();
      setProfile(updatedUser);
      localStorage.setItem("authUser", JSON.stringify(updatedUser));
      navigate("/app/dashboard");
    } catch (error) {
      console.error("Error completing onboarding:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, navigate, setProfile]);

  const renderStep = useCallback(() => {
    switch (step) {
      case 1:
        return <MemoizedWelcomeScreen onProceed={nextStep} />;
      case 2:
        return <MemoizedTerms termsText="Please review and accept the terms and conditions to continue." onAccept={nextStep} />;
      case 3:
        return <MemoizedBiometricVerification onVerified={nextStep} />;
      case 4:
        return <MemoizedVoiceVerification onVerified={nextStep} />;
      case 5:
        return <MemoizedAuthSuccess onFinish={handleFinish} />;
      default:
        return null;
    }
  }, [step, nextStep, handleFinish]);

  return (
    <FlowContainer>
      <h2 style={{ textAlign: "center", color: "red" }}>Onboarding Flow Loaded</h2>
      {renderStep()}
      <ProgressIndicator>Step {step} of 5</ProgressIndicator>
    </FlowContainer>
  );
};

export default memo(OnboardingFlow);
